import { useEffect, useState } from 'react';
import { motion, animate, useReducedMotion } from 'framer-motion';
import { getScoreColor, getScoreLabel } from '../../utils/scoreColors';
import Tooltip from '../common/Tooltip';

interface GreenScoreGaugeProps {
  score: number;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

const sizes = {
  sm: { outer: 64, stroke: 5, fontSize: 'text-lg', labelSize: 'text-[9px]' },
  md: { outer: 96, stroke: 6, fontSize: 'text-2xl', labelSize: 'text-[10px]' },
  lg: { outer: 140, stroke: 8, fontSize: 'text-4xl', labelSize: 'text-xs' },
};

// Counts the score up from 0 in sync with the arc sweep
function AnimatedScore({ score }: { score: number }) {
  const [display, setDisplay] = useState(0);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      setDisplay(Math.round(score));
      return;
    }
    const controls = animate(0, score, {
      duration: 0.9,
      ease: 'easeOut',
      delay: 0.2,
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [score, prefersReducedMotion]);

  return <span className="tabular-nums">{display}</span>;
}

export default function GreenScoreGauge({ score, size = 'md', showLabel = true }: GreenScoreGaugeProps) {
  const { outer, stroke, fontSize, labelSize } = sizes[size];
  const radius = (outer - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = (score / 100) * circumference;
  const color = getScoreColor(score);

  return (
    <Tooltip content="ESG score 0–100: 80+ Excellent · 60–79 Good · 40–59 Fair · 0–39 Poor" position="bottom">
      <div className="relative inline-flex items-center justify-center" style={{ width: outer, height: outer }}>
        <svg width={outer} height={outer} className="-rotate-90">
          {/* Background track */}
          <circle
            cx={outer / 2}
            cy={outer / 2}
            r={radius}
            fill="none"
            stroke="rgba(255,255,255,0.06)"
            strokeWidth={stroke}
          />
          {/* Progress arc */}
          <motion.circle
            cx={outer / 2}
            cy={outer / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            initial={{ strokeDashoffset: circumference }}
            animate={{ strokeDashoffset: circumference - progress }}
            transition={{ duration: 1, ease: 'easeOut', delay: 0.2 }}
            style={{ filter: `drop-shadow(0 0 6px ${color}40)` }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <motion.span
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className={`${fontSize} font-display font-bold`}
            style={{ color }}
          >
            <AnimatedScore score={score} />
          </motion.span>
          {showLabel && (
            <span className={`${labelSize} text-surface-500 font-medium mt-0.5`}>
              {getScoreLabel(score)}
            </span>
          )}
        </div>
      </div>
    </Tooltip>
  );
}
